import React, { useState, useEffect, useMemo } from 'react';
import '../App.css';
import '../assets/Orders.css';
import { OrderCard } from '../components/OrderCard';
import { RouteModal } from '../components/RouteModal';
import logoIcon from '../assets/logo/icon.svg';
import { useData, Order } from '../context/DataContext';
import { API_BASE_URL } from '../utils/api';
import { routeBlocker } from '../utils/routability';

const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const WarnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InboxIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

export const Orders: React.FC = () => {
  const { orders, ordersLoading: loading, ordersError: error, refreshOrders, markOrderPickedUp } = useData();
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'active' | 'past'>('active');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await refreshOrders();
    setRefreshing(false);
  };

  useEffect(() => {
    const saved = localStorage.getItem('selectedOrders');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) setSelectedOrders(new Set(parsed));
    } catch {
      /* stale value from an older build; ignore */
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('selectedOrders', JSON.stringify(Array.from(selectedOrders)));
  }, [selectedOrders]);

  const visible = useMemo(
    () => orders.filter(o => (activeTab === 'active' ? o.status !== 'delivered' : o.status === 'delivered')),
    [orders, activeTab],
  );

  const blockers = useMemo(() => {
    const map = new Map<string, string>();
    visible.forEach(o => {
      const reason = routeBlocker(o);
      if (reason) map.set(o.order_number, reason);
    });
    return map;
  }, [visible]);

  const routable = useMemo(
    () => visible.filter(o => !blockers.has(o.order_number)),
    [visible, blockers],
  );

  /* An order can lose its selection between renders (delivered elsewhere, or
     it turns out to have no location) — never send a stale number to the
     optimizer. */
  const selectableNumbers = useMemo(
    () => new Set(routable.map(o => o.order_number)),
    [routable],
  );
  const validSelection = useMemo(
    () => Array.from(selectedOrders).filter(n => selectableNumbers.has(n)),
    [selectedOrders, selectableNumbers],
  );

  useEffect(() => {
    if (validSelection.length !== selectedOrders.size) {
      setSelectedOrders(new Set(validSelection));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validSelection.length, selectedOrders.size]);

  const handleOrderSelection = (orderNumber: string, isSelected: boolean) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (isSelected) next.add(orderNumber);
      else next.delete(orderNumber);
      return next;
    });
  };

  const allRoutableSelected = routable.length > 0 && validSelection.length === routable.length;
  const toggleSelectAll = () =>
    setSelectedOrders(allRoutableSelected ? new Set() : new Set(routable.map(o => o.order_number)));

  const handleMarkDelivered = async (orderId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${orderId}/mark-delivered`, {
        method: 'PATCH',
        credentials: 'include',
      });
      const result = await res.json();
      if (result.success) refreshOrders();
      else console.error('Failed to mark order as delivered:', result.message);
    } catch (err) {
      console.error('Error marking order as delivered:', err);
    }
  };

  const handleMarkPickedUp = async (orderId: string) => {
    markOrderPickedUp(orderId); // optimistic
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${orderId}/mark-picked-up`, {
        method: 'PATCH',
        credentials: 'include',
      });
      const result = await res.json();
      if (!result.success) {
        console.error('Failed to mark order as picked up:', result.message);
        refreshOrders();
      }
    } catch (err) {
      console.error('Error marking order as picked up:', err);
      refreshOrders();
    }
  };

  const grouped = useMemo(() => {
    const byLocation = visible.reduce((acc, order) => {
      const key = order.location || 'Unknown location';
      (acc[key] ||= []).push(order);
      return acc;
    }, {} as Record<string, Order[]>);
    return Object.entries(byLocation);
  }, [visible]);

  const blockedCount = blockers.size;

  return (
    <div className="page">
      <header className="page__head">
        <img src={logoIcon} alt="" className="page__logo"
             onError={e => { e.currentTarget.style.display = 'none'; }} />
        <div className="page__titles">
          <h1 className="page__title">Orders</h1>
          <p className="page__sub">
            {visible.length} {activeTab === 'active' ? 'active' : 'past'}
            {activeTab === 'active' && blockedCount > 0 && ` · ${blockedCount} not routable`}
            {validSelection.length > 0 && ` · ${validSelection.length} selected`}
          </p>
        </div>
        <div className="page__actions">
          {activeTab === 'active' && routable.length > 0 && (
            <button className="btn btn--ghost btn--sm" onClick={toggleSelectAll}>
              {allRoutableSelected ? 'Clear' : 'Select all'}
            </button>
          )}
          <button
            className={`icon-btn ${refreshing || loading ? 'is-spinning' : ''}`}
            onClick={handleManualRefresh}
            disabled={refreshing || loading}
            aria-label="Refresh orders"
          >
            <RefreshIcon />
          </button>
        </div>
      </header>

      <div className="segmented">
        {(['active', 'past'] as const).map(tab => (
          <button
            key={tab}
            className={`segmented__btn ${activeTab === tab ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'active' ? 'Active' : 'Past'}
          </button>
        ))}
      </div>

      {activeTab === 'active' && blockedCount > 0 && (
        <div className="banner banner--warn">
          <WarnIcon />
          <span>
            <span className="banner__title">
              {blockedCount} order{blockedCount > 1 ? 's' : ''} can’t be routed.
            </span>{' '}
            They have no delivery location, so they can’t be selected or optimized.
            Ask the customer to share their location on WhatsApp.
          </span>
        </div>
      )}

      {loading && orders.length === 0 ? (
        <div className="ocard__list">
          {[0, 1, 2, 3].map(i => <div key={i} className="skeleton ocard__skeleton" />)}
        </div>
      ) : error ? (
        <div className="state state--error">
          <div className="state__icon"><WarnIcon /></div>
          <p className="state__title">Couldn’t load orders</p>
          <p className="state__body">{error}</p>
          <button onClick={handleManualRefresh} className="btn btn--secondary">Try again</button>
        </div>
      ) : visible.length === 0 ? (
        <div className="state">
          <div className="state__icon"><InboxIcon /></div>
          <p className="state__title">No {activeTab} orders</p>
          <p className="state__body">
            {activeTab === 'active'
              ? 'New orders from shops on WhatsApp will land here.'
              : 'Delivered orders move here.'}
          </p>
        </div>
      ) : (
        grouped.map(([location, list]) => (
          <section key={location} className="ogroup">
            <div className="ogroup__head">
              <h2 className="ogroup__title">{location}</h2>
              <span className="ogroup__count">{list.length}</span>
            </div>
            <div className="ocard__list">
              {list.map(order => (
                <OrderCard
                  key={order.order_number}
                  orderId={order.order_number}
                  orderNumber={order.order_number}
                  storeName={order.shop_name}
                  customerName={(order as any).customer_name}
                  preferredDeliveryTime={order.time_slot}
                  orderStatus={order.status}
                  assignedDriver={order.assigned_driver}
                  paymentStatus={order.payment_status}
                  pickedUp={order.picked_up}
                  isReturn={order.is_return}
                  returnReason={order.return_reason}
                  blockedReason={activeTab === 'active' ? blockers.get(order.order_number) ?? null : null}
                  isSelected={selectedOrders.has(order.order_number)}
                  onSelectionChange={activeTab === 'active' ? handleOrderSelection : undefined}
                  onMarkDelivered={activeTab === 'active' ? handleMarkDelivered : undefined}
                  onMarkPickedUp={activeTab === 'active' ? handleMarkPickedUp : undefined}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {validSelection.length > 0 && activeTab === 'active' && (
        <div className="selbar" role="region" aria-label="Selection actions">
          <div className="selbar__inner">
            <span className="selbar__count">
              {validSelection.length} order{validSelection.length > 1 ? 's' : ''} selected
            </span>
            <button className="btn btn--ghost btn--sm" onClick={() => setSelectedOrders(new Set())}>
              Clear
            </button>
            <button className="btn btn--primary" onClick={() => setShowAssignModal(true)}>
              Plan route
            </button>
          </div>
        </div>
      )}

      {showAssignModal && (
        <RouteModal
          selectedOrders={validSelection}
          onClose={() => setShowAssignModal(false)}
          onAssignSuccess={() => {
            setSelectedOrders(new Set());
            refreshOrders();
          }}
        />
      )}
    </div>
  );
};
